import { useMemo, useRef, useState } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { FaBold, FaItalic, FaCode, FaLink, FaImage, FaListUl, FaListOl, FaQuoteRight, FaHeading, FaEye, FaColumns, FaPen, FaStrikethrough } from 'react-icons/fa';

const BLOCK = 'LStudio__app_rte';

/*
    🔥 EDITOR — Markdown

    El más honesto de los tres: lo que escribís es lo que se guarda.
    Sin HTML sucio, sin estilos pegados al copiar desde Word, y
    versionable en git si hiciera falta.

    ⚠️ EL SANITIZADO NO ES OPCIONAL
       marked convierte markdown en HTML, y markdown ACEPTA HTML crudo.
       Sin DOMPurify, un usuario podría escribir <script> y ejecutarlo
       en la sesión de otro. Es el clásico XSS almacenado.

    npm i marked dompurify
*/

const MODES = [
    { id: 'write', label: 'Escribir', icon: <FaPen /> },
    { id: 'split', label: 'Dividido', icon: <FaColumns /> },
    { id: 'preview', label: 'Vista', icon: <FaEye /> },
];

/*
    Envuelve la selección con marcas, o inserta la plantilla si no hay
    nada seleccionado. Es lo que hace que la barra sirva de verdad.
*/
const BLOCK_PREFIX = /^(\s*)(?:#{1,6}\s+|>\s+|[-*+]\s+|\d+\.\s+)/;
const INLINE_FORMATS = [
    { before: '**', after: '**' },
    { before: '_', after: '_' },
    { before: '~~', after: '~~' },
    { before: '`', after: '`' },
];

const onStripInline = (text = '') => {
    let NEXT = text;
    let PREVIOUS = null;

    while (NEXT !== PREVIOUS) {
        PREVIOUS = NEXT;
        NEXT = NEXT
            .replace(/\*\*([^\n]*?)\*\*/g, '$1')
            .replace(/~~([^\n]*?)~~/g, '$1')
            .replace(/`([^\n]*?)`/g, '$1')
            .replace(/_([^\n]*?)_/g, '$1');
    }

    return NEXT;
};

const onGetLineBounds = (value, start, end = start) => {
    const LINE_START = value.lastIndexOf('\n', Math.max(0, start - 1)) + 1;
    const NEXT_BREAK = value.indexOf('\n', end);
    const LINE_END = NEXT_BREAK === -1 ? value.length : NEXT_BREAK;
    return { LINE_START, LINE_END };
};

const onIsHeadingAt = (value, position) => {
    const { LINE_START, LINE_END } = onGetLineBounds(value, position);
    return /^\s*#{1,6}\s+/.test(value.slice(LINE_START, LINE_END));
};

const onWrapStrict = (area, value, onChange, before, after = before) => {
    let START = area.selectionStart;
    let END = area.selectionEnd;
    if (END <= START) return;

    const SELECTED_RAW = value.slice(START, END);
    if (SELECTED_RAW.includes('\n')) return;
    if (onIsHeadingAt(value, START)) return;

    const { LINE_START } = onGetLineBounds(value, START, END);
    const PREFIX_MATCH = value.slice(LINE_START).match(BLOCK_PREFIX);
    if (PREFIX_MATCH) {
        const PREFIX_END = LINE_START + PREFIX_MATCH[0].length;
        if (START < PREFIX_END) START = PREFIX_END;
        if (END <= START) return;
    }

    const SELECTED = value.slice(START, END);
    const HAS_INSIDE = SELECTED.startsWith(before) && SELECTED.endsWith(after)
        && SELECTED.length >= before.length + after.length;
    const HAS_OUTSIDE = value.slice(Math.max(0, START - before.length), START) === before
        && value.slice(END, END + after.length) === after;

    let CLEAN = onStripInline(SELECTED);
    let NEXT;
    let NEXT_START;
    let NEXT_END;

    if (HAS_INSIDE) {
        CLEAN = onStripInline(SELECTED.slice(before.length, SELECTED.length - after.length));
        NEXT = `${value.slice(0, START)}${CLEAN}${value.slice(END)}`;
        NEXT_START = START;
        NEXT_END = START + CLEAN.length;
    } else if (HAS_OUTSIDE) {
        const WRAP_START = START - before.length;
        const WRAP_END = END + after.length;
        CLEAN = onStripInline(SELECTED);
        NEXT = `${value.slice(0, WRAP_START)}${CLEAN}${value.slice(WRAP_END)}`;
        NEXT_START = WRAP_START;
        NEXT_END = WRAP_START + CLEAN.length;
    } else {
        NEXT = `${value.slice(0, START)}${before}${CLEAN}${after}${value.slice(END)}`;
        NEXT_START = START + before.length;
        NEXT_END = NEXT_START + CLEAN.length;
    }

    onChange?.(NEXT);

    requestAnimationFrame(() => {
        area.focus();
        area.selectionStart = NEXT_START;
        area.selectionEnd = NEXT_END;
    });
};

const onCleanBlockPrefix = (line) => {
    let NEXT = line;
    let PREVIOUS = null;

    while (NEXT !== PREVIOUS) {
        PREVIOUS = NEXT;
        NEXT = NEXT.replace(BLOCK_PREFIX, '$1');
    }

    return NEXT;
};
const onCleanHeadingInline = (line) => {
    const MATCH = line.match(/^(\s*)(.*)$/);
    if (!MATCH) return line;
    return `${MATCH[1]}${onStripInline(MATCH[2])}`;
};

const onToggleLineFormat = (area, value, onChange, type) => {
    const START = area.selectionStart;
    const END = area.selectionEnd;
    const LINE_START = value.lastIndexOf('\n', Math.max(0, START - 1)) + 1;
    const END_AT_LINE_START = END > START && value[END - 1] === '\n';
    const SEARCH_END = END_AT_LINE_START ? END - 1 : END;
    const NEXT_BREAK = value.indexOf('\n', SEARCH_END);
    const LINE_END = NEXT_BREAK === -1 ? value.length : NEXT_BREAK;
    const BLOCK_TEXT = value.slice(LINE_START, LINE_END);
    const LINES = BLOCK_TEXT.split('\n');
    const NON_EMPTY = LINES.filter((line) => line.trim().length > 0);

    const IS_APPLIED = {
        heading: (line) => /^\s*#{1,6}\s+/.test(line),
        bullet: (line) => /^\s*[-*+]\s+/.test(line),
        ordered: (line) => /^\s*\d+\.\s+/.test(line),
        quote: (line) => /^\s*>\s?/.test(line),
    }[type];

    if (!IS_APPLIED || NON_EMPTY.length === 0) return;

    const SHOULD_REMOVE = NON_EMPTY.every(IS_APPLIED);
    let ITEM_INDEX = 0;

    const NEXT_LINES = LINES.map((line) => {
        if (!line.trim()) return line;

        let CLEAN = onCleanBlockPrefix(line);
        if (type === 'heading') CLEAN = onCleanHeadingInline(CLEAN);
        if (SHOULD_REMOVE) return CLEAN;

        if (type === 'heading') return CLEAN.replace(/^(\s*)/, '$1## ');
        if (type === 'bullet') return CLEAN.replace(/^(\s*)/, '$1- ');
        if (type === 'ordered') {
            ITEM_INDEX += 1;
            return CLEAN.replace(/^(\s*)/, `$1${ITEM_INDEX}. `);
        }
        if (type === 'quote') return CLEAN.replace(/^(\s*)/, '$1> ');
        return CLEAN;
    });

    const NEXT_BLOCK = NEXT_LINES.join('\n');
    const NEXT_VALUE = `${value.slice(0, LINE_START)}${NEXT_BLOCK}${value.slice(LINE_END)}`;
    onChange?.(NEXT_VALUE);

    requestAnimationFrame(() => {
        area.focus();
        const DELTA = NEXT_BLOCK.length - BLOCK_TEXT.length;
        area.selectionStart = Math.max(LINE_START, START + Math.min(0, DELTA));
        area.selectionEnd = Math.max(area.selectionStart, END + DELTA);
    });
};

export const RichTextMarkdown = ({
    value = '', onChange, placeholder = 'Escribí en Markdown…',
    isDisabled = false, minHeight = 200, defaultMode = 'split',
}) => {

    const REF__useRefArea = useRef(null);

    const [useMode, setMode] = useState(defaultMode);

    const MEMO__HTML = useMemo(() => {

        if (!value) return '';

        // ⚠️ marked → DOMPurify. Nunca uno sin el otro.
        return DOMPurify.sanitize(marked.parse(value, { breaks: true, gfm: true }));

    }, [value]);

    const onEmit = (next) => onChange?.({ target: { value: next } });

    const onAction = (fn) => () => {
        const AREA = REF__useRefArea.current;
        if (!AREA || isDisabled) return;
        fn(AREA, value, onEmit);
    };

    const onKeyDown = (event) => {

        // Tab inserta dos espacios en vez de saltar de campo
        if (event.key === 'Tab') {
            event.preventDefault();
            const AREA = event.target;
            const POS = AREA.selectionStart;
            onEmit(`${value.slice(0, POS)}  ${value.slice(AREA.selectionEnd)}`);
            requestAnimationFrame(() => { AREA.selectionStart = AREA.selectionEnd = POS + 2; });
        }
    };

    return (
        <div className={`${BLOCK} ${BLOCK}--markdown`} data-mode={useMode} data-disabled={isDisabled}
            style={{ '--LS__rte_min_height': `${minHeight}px` }} >

            <div className={`${BLOCK}__toolbar`}>

                <div className={`${BLOCK}__group`}>
                    <button type="button" className={`${BLOCK}__tool`} title="Título" onMouseDown={(event) => event.preventDefault()}
                        onClick={onAction((a, v, c) => onToggleLineFormat(a, v, c, 'heading'))}><FaHeading /></button>
                    <button type="button" className={`${BLOCK}__tool`} title="Negrita" onMouseDown={(event) => event.preventDefault()}
                        onClick={onAction((a, v, c) => onWrapStrict(a, v, c, '**'))}><FaBold /></button>
                    <button type="button" className={`${BLOCK}__tool`} title="Cursiva" onMouseDown={(event) => event.preventDefault()}
                        onClick={onAction((a, v, c) => onWrapStrict(a, v, c, '_'))}><FaItalic /></button>
                    <button type="button" className={`${BLOCK}__tool`} title="Tachado" onMouseDown={(event) => event.preventDefault()}
                        onClick={onAction((a, v, c) => onWrapStrict(a, v, c, '~~'))}><FaStrikethrough /></button>
                    <button type="button" className={`${BLOCK}__tool`} title="Código" onMouseDown={(event) => event.preventDefault()}
                        onClick={onAction((a, v, c) => onWrapStrict(a, v, c, '`'))}><FaCode /></button>
                </div>

                <div className={`${BLOCK}__group`}>
                    <button type="button" className={`${BLOCK}__tool`} title="Lista" onMouseDown={(event) => event.preventDefault()}
                        onClick={onAction((a, v, c) => onToggleLineFormat(a, v, c, 'bullet'))}><FaListUl /></button>
                    <button type="button" className={`${BLOCK}__tool`} title="Lista numerada" onMouseDown={(event) => event.preventDefault()}
                        onClick={onAction((a, v, c) => onToggleLineFormat(a, v, c, 'ordered'))}><FaListOl /></button>
                    <button type="button" className={`${BLOCK}__tool`} title="Cita" onMouseDown={(event) => event.preventDefault()}
                        onClick={onAction((a, v, c) => onToggleLineFormat(a, v, c, 'quote'))}><FaQuoteRight /></button>
                </div>

                <div className={`${BLOCK}__group`}>
                    <button type="button" className={`${BLOCK}__tool`} title="Enlace" onMouseDown={(event) => event.preventDefault()}
                        onClick={onAction((a, v, c) => onWrapStrict(a, v, c, '[', '](https://)'))}><FaLink /></button>
                    <button type="button" className={`${BLOCK}__tool`} title="Imagen" onMouseDown={(event) => event.preventDefault()}
                        onClick={onAction((a, v, c) => onWrapStrict(a, v, c, '![', '](https://)'))}><FaImage /></button>
                </div>

                <div className={`${BLOCK}__group ${BLOCK}__group--end`}>
                    {
                        MODES.map((mode) => (
                            <button key={mode.id} type="button" className={`${BLOCK}__tool`}
                                data-on={useMode === mode.id} title={mode.label}
                                onClick={() => setMode(mode.id)} >
                                {mode.icon}
                            </button>
                        ))
                    }
                </div>

            </div>

            <div className={`${BLOCK}__panes`}>

                {
                    useMode !== 'preview' && (
                        <textarea ref={REF__useRefArea} className={`${BLOCK}__source`}
                            value={value} onChange={onChange} onKeyDown={onKeyDown}
                            placeholder={placeholder} disabled={isDisabled}
                            spellCheck="false" autoComplete="off" />
                    )
                }

                {
                    useMode !== 'write' && (
                        <div className={`${BLOCK}__preview ${BLOCK}__content`}
                            dangerouslySetInnerHTML={{ __html: MEMO__HTML }} />
                    )
                }

            </div>

        </div>
    );
};
